import { useTranslation } from 'react-i18next';
import type {
  AssistantReplyLength,
  AssistantTone,
} from '@/api/assistantConfiguration/assistantConfigurationApi.types';
import { ASSISTANT_REPLY_LENGTHS, ASSISTANT_TONES } from './assistantConfigurationOptions';
import { OptionChips, type OptionChip } from './OptionChips';

interface BehaviourFieldsProps {
  tone: AssistantTone | null;
  replyLength: AssistantReplyLength | null;
  inheritedTone: AssistantTone | null;
  inheritedReplyLength: AssistantReplyLength | null;
  onToneChange: (nextTone: AssistantTone | null) => void;
  onReplyLengthChange: (nextReplyLength: AssistantReplyLength | null) => void;
}

export function BehaviourFields({
  tone,
  replyLength,
  inheritedTone,
  inheritedReplyLength,
  onToneChange,
  onReplyLengthChange,
}: BehaviourFieldsProps) {
  const { t } = useTranslation('assistant');

  function buildOptions<Value extends string>(
    values: Value[],
    labelPrefix: string,
    inheritedValue: Value | null
  ): OptionChip<Value | null>[] {
    const valueOptions: OptionChip<Value | null>[] = values.map(function toOption(value) {
      return { value, label: t(labelPrefix + value) };
    });
    if (inheritedValue === null) {
      return valueOptions;
    }
    const inheritOption: OptionChip<Value | null> = {
      value: null,
      label: t('configuration.defaultOption', { value: t(labelPrefix + inheritedValue) }),
    };
    return [inheritOption, ...valueOptions];
  }

  return (
    <div className="space-y-5">
      <OptionChips
        label={t('configuration.tone.label')}
        options={buildOptions(ASSISTANT_TONES, 'configuration.tone.options.', inheritedTone)}
        selectedValue={tone}
        onSelect={onToneChange}
      />
      <OptionChips
        label={t('configuration.replyLength.label')}
        options={buildOptions(
          ASSISTANT_REPLY_LENGTHS,
          'configuration.replyLength.options.',
          inheritedReplyLength
        )}
        selectedValue={replyLength}
        onSelect={onReplyLengthChange}
      />
    </div>
  );
}
